import { useSelector } from 'react-redux';

import { LTL_SUPPORTED_COUNTRY_CODES } from '@shipstation/client-common/ltl/constants';
import type { ShipFrom } from '@shipstation/client-common/types/ship-from';

import Global from '../../store/global-selectors';
import { getShipFromIdOrDefault } from '../../fulfillment-planner/helpers/get-ship-from';

type FulfillmentPlanLocation = {
  shipFromId?: string | null;
  sellerFulfillmentProviderId?: string | null;
  shipTo?: { countryCode?: string | null } | null;
};

const isUnsupportedLtlCountry = (countryCode: string | null | undefined) =>
  !!countryCode && !LTL_SUPPORTED_COUNTRY_CODES.includes(countryCode.toUpperCase());

// Only own-warehouse plans fall back to the default ship-from, matching what the UI displays;
// a fulfillment-provider plan without a shipFromId is not shipped from the seller's default warehouse.
const resolveShipFromCountryCode = (fulfillmentPlan: FulfillmentPlanLocation, shipFroms: ShipFrom[]) => {
  const shipFromId = fulfillmentPlan.sellerFulfillmentProviderId
    ? fulfillmentPlan.shipFromId
    : getShipFromIdOrDefault(fulfillmentPlan.shipFromId ?? null, shipFroms);
  return shipFroms.find((shipFrom) => String(shipFrom.shipFromId) === String(shipFromId))?.originAddress?.countryCode;
};

export const hasUnsupportedLtlFulfillmentPlan = (fulfillmentPlans: FulfillmentPlanLocation[], shipFroms: ShipFrom[]) =>
  fulfillmentPlans.some(
    (fulfillmentPlan) =>
      isUnsupportedLtlCountry(fulfillmentPlan.shipTo?.countryCode) ||
      isUnsupportedLtlCountry(resolveShipFromCountryCode(fulfillmentPlan, shipFroms)),
  );

export const useCanSwitchToFreight = (fulfillmentPlanIds: readonly (string | number)[]) => {
  const fulfillmentPlans: FulfillmentPlanLocation[] = useSelector((state) =>
    Global.selectEntitiesFulfillmentPlansByIds(state, { fulfillmentPlanIds }),
  );
  const shipFroms: ShipFrom[] = useSelector(Global.selectSellerShipFroms);

  return !hasUnsupportedLtlFulfillmentPlan(fulfillmentPlans, shipFroms);
};
